import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import AlertBox from "components/Alert/alert";

// Data
import indexTableData from "layouts/indexes/data/indexTableData";

// APIs
import { getAllIndexes, insertActivity, editActivity, enableActivity, deleteActivity, updateIndexOrder } from "services/apiService";
import { Switch } from "@mui/material";

function IndexManagement() {
  const [people, setPeople] = useState([]);
  const [alert, setAlert] = useState({ open: false, type: "success", title: "", message: "" });

  // ── Add dialog state ──────────────────────────────────────────────
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newName, setNewName] = useState("");
  const [description, setDescription] = useState("");
  const [searchText, setSearchText] = useState("");
  const [isJobType, setIsJobType] = useState(false);
  const [newImage, setNewImage] = useState(null);       // File object
  const [newImagePreview, setNewImagePreview] = useState(null);
  const addFileRef = useRef(null);

  // ── Edit dialog state ─────────────────────────────────────────────
  const [selectedItem, setSelectedItem] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [editdescription, setEditDescription] = useState("");
  const [editSearchText, setEditSearchText] = useState("");
  const [editIsJobType, setEditIsJobType] = useState(false);
  const [editImage, setEditImage] = useState(null);      // File object (new upload)
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const editFileRef = useRef(null);

  useEffect(() => {
    getIndex();
  }, []);

  const getIndex = async () => {
    try {
      const response = await getAllIndexes();
      if (response?.success) {
        setPeople(response?.data);
      }
    } catch (err) {
      console.error("Fetch indexes failed:", err);
    }
  };

  // ── Toggle active ─────────────────────────────────────────────────
  const handleToggleActive = async (id, currentStatus) => {
    try {
      const obj = { activityId: id, isEnabled: !currentStatus };
      const response = await enableActivity(obj);
      if (response?.success) {
        getIndex();
        setAlert({ open: true, type: "success", title: "Success", message: response?.message?.message });
      } else {
        setAlert({ open: true, type: "error", title: "Error", message: response?.message });
      }
    } catch (err) {
      console.error("Toggle failed:", err);
      setAlert({ open: true, type: "error", title: "Error", message: "Failed to update status." });
    }
  };

  // ── Delete Index ──────────────────────────────────────────────────
  const handleDeleteIndex = async (id) => {
    try {
      const response = await deleteActivity({ id });
      if (response?.success) {
        getIndex();
        setAlert({ open: true, type: "success", title: "Deleted", message: "Index deleted successfully." });
      } else {
        setAlert({ open: true, type: "error", title: "Error", message: response?.message });
      }
    } catch (err) {
      setAlert({ open: true, type: "error", title: "Error", message: "Failed to delete index." });
    }
  };

  // ── Add Index ─────────────────────────────────────────────────────
  const handleOpenAdd = () => {
    setNewName("");
    setDescription("");
    setSearchText("");
    setIsJobType(false);
    setNewImage(null);
    setNewImagePreview(null);
    setOpenAddDialog(true);
  };

  const handleAddImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewImage(file);
      setNewImagePreview(URL.createObjectURL(file));
    }
  };

  const handleAddIndex = async () => {
    if (!newName.trim()) return;
    try {
      const formData = new FormData();
      formData.append("name", newName.trim());
      formData.append("description", description);
      formData.append("searchText", searchText);
      formData.append("isJobType", isJobType);
      if (newImage) formData.append("image", newImage);

      const response = await insertActivity(formData);
      if (response?.success) {
        getIndex();
        setAlert({ open: true, type: "success", title: "Added", message: "Index added successfully." });
      } else {
        setAlert({ open: true, type: "error", title: "Error", message: response?.message });
      }
    } catch (err) {
      setAlert({ open: true, type: "error", title: "Error", message: "Failed to add index." });
    } finally {
      setOpenAddDialog(false);
    }
  };

  // ── Edit Index ────────────────────────────────────────────────────
  const handleOpenEdit = (person) => {
    setSelectedItem(person);
    setEditedName(person.name);
    setEditDescription(person.description)
    setEditSearchText(person.searchText);
    setEditIsJobType(person.isJobType);
    setEditImage(null);
    setEditImagePreview(person?.imageUrl || null);
    setOpenEditDialog(true);
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditImage(file);
      setEditImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSaveEdit = async () => {
    if (!editedName.trim()) return;
    try {
      const formData = new FormData();
      formData.append("id", selectedItem?.id);
      formData.append("name", editedName.trim());
      formData.append("description", editdescription);
      formData.append("searchText", editSearchText);
      formData.append("isJobType", editIsJobType);
      if (editImage) formData.append("image", editImage);

      const response = await editActivity(formData);
      if (response?.success) {
        getIndex();
        setAlert({ open: true, type: "success", title: "Updated", message: response?.message?.message || "Index updated." });
      } else {
        setAlert({ open: true, type: "error", title: "Error", message: response?.message });
      }
    } catch (err) {
      setAlert({ open: true, type: "error", title: "Error", message: "Update failed." });
    } finally {
      setOpenEditDialog(false);
    }
  };

  // ── Move Index ────────────────────────────────────────────────────
  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newList = [...people];
    const temp = newList[index - 1];
    newList[index - 1] = newList[index];
    newList[index] = temp;
    setPeople(newList);
    await saveNewOrder(newList);
  };

  const handleMoveDown = async (index) => {
    if (index === people.length - 1) return;
    const newList = [...people];
    const temp = newList[index + 1];
    newList[index + 1] = newList[index];
    newList[index] = temp;
    setPeople(newList);
    await saveNewOrder(newList);
  };

  const saveNewOrder = async (orderedList) => {
    try {
      const payload = {
        orderedIds: orderedList.map(item => item.id)
      };
      const response = await updateIndexOrder(payload);
      if (!response?.success && response?.message) {
        setAlert({ open: true, type: "error", title: "Error", message: response?.message || "Failed to update order" });
      }
    } catch (err) {
      setAlert({ open: true, type: "error", title: "Error", message: "Failed to update order" });
    }
  };

  const { columns, rows } = indexTableData({
    peopleList: people,
    onToggleActive: handleToggleActive,
    onEditClick: handleOpenEdit,
    onDeleteClick: handleDeleteIndex,
    onMoveUp: handleMoveUp,
    onMoveDown: handleMoveDown,
  });

  // ── Shared image-picker UI ────────────────────────────────────────
  const ImagePicker = ({ preview, fileRef, onChange, label }) => (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1} mt={1} mb={1}>
      <Box position="relative">
        <Avatar
          src={preview}
          alt={label}
          sx={{ width: 90, height: 90, border: "2px dashed #bbb", background: "#f5f5f5" }}
        />
        <IconButton
          size="small"
          onClick={() => fileRef.current?.click()}
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            background: "#1976d2",
            color: "#fff",
            "&:hover": { background: "#115293" },
            width: 28,
            height: 28,
          }}
        >
          <PhotoCameraIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      <MDTypography variant="caption" color="text">
        {preview ? "Change Image" : "Upload Image"}
      </MDTypography>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={onChange}
      />
    </Box>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <AlertBox
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
      />

      {/* ── Add Index Dialog ── */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add New Index</DialogTitle>
        <DialogContent>
          <ImagePicker
            preview={newImagePreview}
            fileRef={addFileRef}
            onChange={handleAddImageChange}
            label="Index Image"
          />
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Index Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Text for youtube search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {/* <Switch
            checked={isJobType}
            onChange={(e) => setIsJobType(e.target.checked)}
            color="primary"
          />
          <MDTypography variant="button" fontWeight="medium">
            Is Job Type
          </MDTypography> */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddIndex}
            disabled={!newName.trim()}
            sx={{ color: "beige" }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Index Dialog ── */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Index</DialogTitle>
        <DialogContent>
          <ImagePicker
            preview={editImagePreview}
            fileRef={editFileRef}
            onChange={handleEditImageChange}
            label="Index Image"
          />
          <TextField
            fullWidth
            margin="dense"
            label="Index Name"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Description"
            value={editdescription}
            onChange={(e) => setEditDescription(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Text for youtube search"
            value={editSearchText}
            onChange={(e) => setEditSearchText(e.target.value)}
          />
          {/* <Switch
            checked={editIsJobType}
            onChange={(e) => setEditIsJobType(e.target.checked)}
            color="primary"
          />
          <MDTypography variant="button" fontWeight="medium">
            Is Job Type
          </MDTypography> */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveEdit}
            disabled={!editedName.trim()}
            sx={{ color: "beige" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Main Content ── */}
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Indexes
                </MDTypography>
                <Button
                  variant="contained"
                  onClick={handleOpenAdd}
                  sx={{
                    background: "rgba(255,255,255,0.2)",
                    color: "#fff",
                    fontWeight: "bold",
                    "&:hover": { background: "rgba(255,255,255,0.35)" },
                  }}
                >
                  + Add Index
                </Button>
              </MDBox>

              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  canSearch={false}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default IndexManagement;
